import type { IconName } from "@bundle/tokens";

/** Menu / context-menu / hover-action entry. `label` is already translated. */
export type MenuItem = {
  id: string;
  label: string;
  /** A second, smaller line under the label (Drive's "General access" choices: what it means, or why it's disabled). Menus only. */
  description?: string;
  icon?: IconName;
  destructive?: boolean;
  /**
   * A toggle's current state (e.g. a quick reaction I already added): web hover actions show it active with
   * aria-pressed and stay open after it is pressed; menus may show a checkmark. Absent = an action, not a toggle
   * (pressing it closes the hover toolbar until the pointer leaves the row).
   */
  selected?: boolean;
  /** Shown but not pressable (dimmed; skipped by the keyboard). Menus only; hover actions omit disabled items. */
  disabled?: boolean;
  /** Trailing caption (a shortcut "⌘/", a short note "Answers right away"). Menus only. */
  hint?: string;
  /** Trailing glyph (`external` for a link that leaves the app). Menus only. */
  trailingIcon?: IconName;
  onSelect: () => void;
};

/** A divider between groups of menu items (`Menu.items`, `Pressable.contextMenu`); `heading` names the group instead. */
export type MenuSeparator = { id: string; separator: true; heading?: string };

/** One row of a menu: an item or a separator. */
export type MenuEntry = MenuItem | MenuSeparator;

export function isMenuSeparator(e: MenuEntry): e is MenuSeparator {
  return "separator" in e && e.separator === true;
}

export type Align = "start" | "center" | "end" | "stretch";
export type Justify = "start" | "center" | "end" | "between";
